import { format } from 'date-fns';

import { getTimeZoneAbbr, localDayAt } from '@/utils/time-zone';

const LOCALE = 'en-US';

type Style = 'dateTime' | 'date' | 'monthDay' | 'time';

const OPTIONS: Record<Style, Intl.DateTimeFormatOptions> = {
  dateTime: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
  date: { month: 'short', day: 'numeric', year: 'numeric' },
  monthDay: { month: 'short', day: 'numeric' },
  time: { hour: 'numeric', minute: '2-digit' },
};

// Building a formatter is the costly step, and a page of cards reads one zone.
const formatters = new Map<string, Intl.DateTimeFormat | null>();

function formatAt(instant: Date, timeZone: string, style: Style): string {
  const key = `${timeZone}|${style}`;
  if (!formatters.has(key)) {
    try {
      formatters.set(
        key,
        new Intl.DateTimeFormat(LOCALE, { ...OPTIONS[style], timeZone }),
      );
    } catch {
      formatters.set(key, null);
    }
  }
  return formatters.get(key)?.format(instant) ?? '';
}

/**
 * A campaign's start and end as read at its opening, for the card and its tooltip.
 * @param startsAt - The campaign's start. eg '2026-08-30T14:04:00Z'
 * @param endAt - The campaign's end, or null when it has none. eg '2026-09-25T03:59:00Z'
 * @param timeZone - The opening's IANA zone. eg 'America/Chicago'
 * @returns Display strings, or null for an unknown zone. eg { start: 'Aug 30, 2026, 9:04 AM', end: 'Sep 24, 2026, 10:59 PM', … }
 */
export function getCampaignWindowAt(
  startsAt: string,
  endAt: string | null | undefined,
  timeZone: string,
) {
  const start = new Date(startsAt);
  const startAbbr = getTimeZoneAbbr(start, timeZone);
  if (!startAbbr) return null;

  const end = endAt ? new Date(endAt) : null;
  return {
    start: formatAt(start, timeZone, 'dateTime'),
    startAbbr,
    startTime: formatAt(start, timeZone, 'time'),
    // "28th September", the scheduled card's go-live day at the opening.
    startDayLong: format(localDayAt(start, timeZone) ?? start, 'do MMMM'),
    end: end
      ? {
          dateTime: formatAt(end, timeZone, 'dateTime'),
          monthDay: formatAt(end, timeZone, 'monthDay'),
          time: formatAt(end, timeZone, 'time'),
          abbr: getTimeZoneAbbr(end, timeZone),
        }
      : null,
  };
}

/**
 * The calendar day an instant falls on at the opening: "Aug 30, 2026".
 * @param instant - The moment. eg '2026-08-30T09:04:00-05:00'
 * @param timeZone - The opening's IANA zone. eg 'America/Chicago'
 * @returns The day. eg 'Aug 30, 2026'
 */
export function formatDayAt(instant: string | Date, timeZone: string): string {
  return formatAt(new Date(instant), timeZone, 'date');
}
